import { useState, useEffect } from 'react';
import { Modal, Button, Space } from 'antd';
import { DownloadOutlined, ShareAltOutlined } from '@ant-design/icons';

const DISMISSED_KEY = 'ark_install_dismissed';
const PROMPT_DELAY_MS = 15_000; // show 15s after page load

export const InstallPromptModal = () => {
  const [open, setOpen] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState<any>(null);
  const [isIos, setIsIos] = useState(false);

  useEffect(() => {
    // Already running as installed app
    if (window.matchMedia('(display-mode: standalone)').matches) return;

    // User already dismissed
    if (localStorage.getItem(DISMISSED_KEY)) return;

    const ios =
      /iphone|ipad|ipod/i.test(navigator.userAgent) &&
      !(window as any).MSStream;
    setIsIos(ios);

    if (ios) {
      const t = setTimeout(() => setOpen(true), PROMPT_DELAY_MS);
      return () => clearTimeout(t);
    }

    let timer: ReturnType<typeof setTimeout>;

    const onBeforeInstall = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e);
      timer = setTimeout(() => setOpen(true), PROMPT_DELAY_MS);
    };

    const onInstalled = () => {
      setOpen(false);
      localStorage.setItem(DISMISSED_KEY, 'true');
    };

    window.addEventListener('beforeinstallprompt', onBeforeInstall);
    window.addEventListener('appinstalled', onInstalled);
    return () => {
      clearTimeout(timer);
      window.removeEventListener('beforeinstallprompt', onBeforeInstall);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === 'accepted') {
      localStorage.setItem(DISMISSED_KEY, 'true');
    }
    setDeferredPrompt(null);
    setOpen(false);
  };

  const handleDismiss = () => {
    localStorage.setItem(DISMISSED_KEY, 'true');
    setOpen(false);
  };

  return (
    <Modal
      open={open}
      onCancel={handleDismiss}
      footer={null}
      width={380}
      centered
      closable
    >
      <div style={{ textAlign: 'center', padding: '8px 0 16px' }}>
        <div style={{ fontSize: 40, color: '#2C5F8D', marginBottom: 12 }}>
          {isIos ? <ShareAltOutlined /> : <DownloadOutlined />}
        </div>

        <h3 style={{ margin: '0 0 8px', fontSize: 18 }}>
          Install Rongeka Africa
        </h3>

        {isIos ? (
          <>
            <p style={{ color: '#666', marginBottom: 20 }}>
              Tap the <strong>Share</strong> button at the bottom of your browser,
              then select <strong>"Add to Home Screen"</strong> to install this app.
            </p>
            <Button onClick={handleDismiss}>Got it</Button>
          </>
        ) : (
          <>
            <p style={{ color: '#666', marginBottom: 24 }}>
              Install the portal on your device for quick access - opens in its
              own window without the browser, and always stays up to date.
            </p>
            <Space>
              <Button onClick={handleDismiss}>Not now</Button>
              <Button type="primary" icon={<DownloadOutlined />} onClick={handleInstall}>
                Install
              </Button>
            </Space>
          </>
        )}
      </div>
    </Modal>
  );
};
